import { Application, Assets, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { TILE_SIZE, ZONE_SIGN_MAX_NAMES, type AvatarConfig, type MapData, type Zone } from '@etown/shared';
import { Camera } from '../game/Camera';
import { WORLD, cssHexToNumber } from '../lib/palette';
import type { IWorldRenderer, ViewPose } from './IWorldRenderer';
import type { AvatarRenderState, ZoneOccupancy } from './renderTypes';
import type { AvatarVisual, TileTheme } from './theme/TileTheme';

interface AvatarEntry {
  visual: AvatarVisual;
  label: Text;
  dndBadge: Text;
  root: Container;
  /** Serialisierte Avatar-Konfiguration — Aenderung -> Visual neu erzeugen. */
  configKey: string;
}

interface ZoneSign {
  plaque: Graphics;
  text: Text;
  baseLine: string;
  occupantsLine: string;
}

export type RendererMode = 'play' | 'editor';

/**
 * Gesamter Pixi-Aufbau der Draufsicht: Welt-Container mit Layern in
 * Zeichenreihenfolge (Boden -> Zonen-Toenung -> Waende -> Dekor -> Objekte ->
 * Avatare). Die Optik der Inhalte kommt ausschliesslich aus dem TileTheme.
 */
export class WorldRenderer implements IWorldRenderer {
  /** Orthogonale Projektion dieser Ansicht; folgt im Spiel dem Avatar. */
  readonly projection = new Camera();
  private app: Application | null = null;
  private world = new Container();
  private layers = {
    floor: new Container(),
    zones: new Container(),
    walls: new Container(),
    decor: new Container(),
    objects: new Container(),
    signs: new Container(),
    avatars: new Container(),
    overlay: new Container(),
  };
  private avatarEntries = new Map<string, AvatarEntry>();
  private objectContainers = new Map<string, Container>();
  private objectSizes = new Map<string, { w: number; h: number }>();
  private objectImageSprites = new Map<string, Sprite>();
  private zoneSigns = new Map<string, ZoneSign>();
  private highlights = new Map<string, Graphics>();
  private highlightedObjectId: string | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mapZones: Zone[] = [];
  private deniedOverlay = new Graphics();
  private deniedZoneIds: ReadonlySet<string> = new Set();
  private clickMarker: Graphics | null = null;
  private clickMarkerStart = 0;

  get canvas(): HTMLCanvasElement | null {
    return this.app?.canvas ?? null;
  }

  get overlayLayer(): Container {
    return this.layers.overlay;
  }

  constructor(
    private readonly theme: TileTheme,
    private readonly mode: RendererMode = 'play',
  ) {}

  async mount(host: HTMLElement): Promise<void> {
    const app = new Application();
    await app.init({
      background: WORLD.background,
      resizeTo: host,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio, 2),
      autoDensity: true,
    });
    this.app = app;
    host.appendChild(app.canvas);
    // resizeTo lauscht nur auf window-Resizes — die Gespraechs-Leiste aendert
    // aber die Host-Groesse ohne window-Event. Daher zusaetzlich ein Observer.
    this.resizeObserver = new ResizeObserver(() => app.resize());
    this.resizeObserver.observe(host);
    await this.theme.load(app.renderer);

    this.world.addChild(
      this.layers.floor,
      this.layers.zones,
      this.layers.walls,
      this.layers.decor,
      this.layers.objects,
      this.layers.signs,
      this.layers.avatars,
      this.layers.overlay,
    );
    app.stage.addChild(this.world);
  }

  /** Map (neu) aufbauen — beim Start und nach map:updated. */
  setMap(map: MapData): void {
    if (!this.app) return;
    for (const layer of Object.values(this.layers)) layer.removeChildren();
    this.objectContainers.clear();
    this.objectSizes.clear();
    this.objectImageSprites.clear();
    this.zoneSigns.clear();
    this.highlights.clear();

    const place = (layerName: 'floor' | 'walls' | 'decor', target: Container): void => {
      const grid = map.layers[layerName];
      for (let y = 0; y < map.height; y += 1) {
        for (let x = 0; x < map.width; x += 1) {
          const typeId = grid[y]?.[x];
          if (!typeId) continue;
          const tile = this.theme.createTile(typeId, layerName);
          if (!tile) continue;
          tile.position.set(x * TILE_SIZE, y * TILE_SIZE);
          target.addChild(tile);
        }
      }
    };
    place('floor', this.layers.floor);
    place('walls', this.layers.walls);
    place('decor', this.layers.decor);

    // Zonen fuer die Zutritts-Markierung merken (Overlay wird nach den
    // Zonen-Toenungen hinzugefuegt, damit es sichtbar obenauf liegt)
    this.mapZones = map.zones;
    this.deniedOverlay = new Graphics();

    // Zonen als sanfte Farbflaechen (im Editor deutlicher);
    // Raum-Konferenz-Zonen kraeftiger + mit Rahmen — auf einen Blick erkennbar
    for (const zone of map.zones) {
      const isZoneCall = zone.audio === 'zone';
      const zoneAlpha = this.mode === 'editor' ? 0.28 : isZoneCall ? 0.16 : 0.1;
      for (const r of zone.rects) {
        const tint = new Graphics()
          .roundRect(r.x * TILE_SIZE, r.y * TILE_SIZE, r.w * TILE_SIZE, r.h * TILE_SIZE, 6)
          .fill({ color: cssHexToNumber(zone.color), alpha: zoneAlpha });
        if (isZoneCall && this.mode === 'play') {
          tint
            .roundRect(r.x * TILE_SIZE + 2, r.y * TILE_SIZE + 2, r.w * TILE_SIZE - 4, r.h * TILE_SIZE - 4, 6)
            .stroke({ width: 3, color: cssHexToNumber(zone.color), alpha: 0.5 });
        }
        this.layers.zones.addChild(tint);
      }
      if (this.mode === 'play') this.createZoneSign(zone);
    }
    this.layers.zones.addChild(this.deniedOverlay);
    this.redrawDeniedZones();

    // Editor-Zusatz: Raster, Kollisionsmarkierungen und Spawn-Punkt
    if (this.mode === 'editor') {
      const grid = new Graphics();
      for (let x = 0; x <= map.width; x += 1) {
        grid.moveTo(x * TILE_SIZE, 0).lineTo(x * TILE_SIZE, map.height * TILE_SIZE);
      }
      for (let y = 0; y <= map.height; y += 1) {
        grid.moveTo(0, y * TILE_SIZE).lineTo(map.width * TILE_SIZE, y * TILE_SIZE);
      }
      grid.stroke({ width: 1, color: 0xffffff, alpha: 0.08 });
      this.layers.overlay.addChild(grid);

      const collisionMarks = new Graphics();
      for (let y = 0; y < map.height; y += 1) {
        const row = map.collision[y] ?? '';
        for (let x = 0; x < map.width; x += 1) {
          if (row[x] === '1') {
            collisionMarks.rect(x * TILE_SIZE + 2, y * TILE_SIZE + 2, TILE_SIZE - 4, TILE_SIZE - 4);
          }
        }
      }
      collisionMarks.fill({ color: 0xe0454e, alpha: 0.22 });
      this.layers.overlay.addChild(collisionMarks);

      for (const spawn of map.spawns) {
        const marker = new Graphics()
          .circle(spawn.x * TILE_SIZE, spawn.y * TILE_SIZE, TILE_SIZE * 0.3)
          .stroke({ width: 3, color: 0x3bb273 });
        this.layers.overlay.addChild(marker);
      }
    }

    for (const obj of map.objects) {
      const container = this.theme.createObject(obj);
      container.position.set(obj.x * TILE_SIZE, obj.y * TILE_SIZE);
      this.layers.objects.addChild(container);
      this.objectContainers.set(obj.id, container);
      this.objectSizes.set(obj.id, { w: obj.w * TILE_SIZE, h: obj.h * TILE_SIZE });
    }
    // Die Hervorhebung entsteht erst, wenn eine gebraucht wird — es ist immer
    // hoechstens eine sichtbar (siehe setObjectHighlight)
    if (this.highlightedObjectId) this.showHighlight(this.highlightedObjectId);
  }

  /** Rahmen um genau ein Objekt; legt ihn beim ersten Mal an. */
  private showHighlight(objectId: string): void {
    const container = this.objectContainers.get(objectId);
    const size = this.objectSizes.get(objectId);
    if (!container || !size) return;
    let highlight = this.highlights.get(objectId);
    if (!highlight || highlight.destroyed) {
      highlight = new Graphics()
        .roundRect(-3, -3, size.w + 6, size.h + 6, 6)
        .stroke({ width: 3, color: WORLD.screenGlow });
      container.addChildAt(highlight, 0);
      this.highlights.set(objectId, highlight);
    }
    highlight.visible = true;
  }

  /** Raumschild an der Tuer einer Zone (Team-/Raumname + Anwesende). */
  private createZoneSign(zone: Zone): void {
    // Zonen ohne Schildposition, ohne Team, offen (z. B. Flur) tragen kein Schild
    if (!zone.sign && !zone.team && zone.kind === 'open') return;
    const rect = zone.rects[0];
    if (!rect) return;
    // Schild haengt an der Tuer (sign) oder — als Fallback — an der Oberkante
    const signX = zone.sign ? zone.sign.x + 0.5 : rect.x + rect.w / 2;
    const signY = zone.sign ? zone.sign.y : rect.y;

    // Zutrittsbeschraenkte Raeume mit Schloss kennzeichnen
    const label = (zone.restricted ? '🔒 ' : '') + (zone.team ?? zone.name);
    const plaque = new Graphics();
    const text = new Text({
      text: label,
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        fontWeight: '600',
        fill: 0xffffff,
        align: 'center',
      },
    });
    text.anchor.set(0.5, 0.5);

    const root = new Container();
    root.position.set(signX * TILE_SIZE, signY * TILE_SIZE + TILE_SIZE * 0.35);
    root.addChild(plaque, text);
    this.layers.signs.addChild(root);

    const sign: ZoneSign = { plaque, text, baseLine: label, occupantsLine: '' };
    this.zoneSigns.set(zone.id, sign);
    this.redrawZoneSign(sign);
  }

  private redrawZoneSign(sign: ZoneSign): void {
    sign.text.text = sign.occupantsLine ? `${sign.baseLine}\n${sign.occupantsLine}` : sign.baseLine;
    const w = sign.text.width + 14;
    const h = sign.text.height + 8;
    sign.plaque
      .clear()
      .roundRect(-w / 2, -h / 2, w, h, 5)
      .fill({ color: WORLD.shadow, alpha: 0.82 });
  }

  /** Verbotene Zonen (fuer mich) rot markieren. */
  /**
   * Die Draufsicht zeigt Whiteboards weiterhin als Symbol — aus der
   * Vogelperspektive waere der Inhalt ohnehin nicht lesbar.
   */
  setObjectBoard(): void {
    /* absichtlich leer */
  }

  /** Die Einstellungen der Ego-Ansicht betreffen die Draufsicht nicht. */
  applySettings(): void {
    /* absichtlich leer */
  }

  /** Pixi fuehrt keine vergleichbare Statistik. */
  renderStats(): null {
    return null;
  }

  setDeniedZones(ids: ReadonlySet<string>): void {
    if (!this.app) return;
    this.deniedZoneIds = ids;
    this.redrawDeniedZones();
  }

  private redrawDeniedZones(): void {
    this.deniedOverlay.clear();
    for (const zone of this.mapZones) {
      if (!this.deniedZoneIds.has(zone.id)) continue;
      for (const r of zone.rects) {
        this.deniedOverlay
          .roundRect(r.x * TILE_SIZE, r.y * TILE_SIZE, r.w * TILE_SIZE, r.h * TILE_SIZE, 6)
          .fill({ color: 0xe0454e, alpha: 0.16 })
          .roundRect(r.x * TILE_SIZE + 2, r.y * TILE_SIZE + 2, r.w * TILE_SIZE - 4, r.h * TILE_SIZE - 4, 6)
          .stroke({ width: 3, color: 0xe0454e, alpha: 0.6 });
      }
    }
  }

  /** Belegungszeile der Raumschilder aktualisieren (Dirty-Check pro Zone). */
  syncZoneSigns(occupancy: ZoneOccupancy): void {
    for (const [zoneId, sign] of this.zoneSigns) {
      const occupants = occupancy.get(zoneId) ?? [];
      const names = occupants.slice(0, ZONE_SIGN_MAX_NAMES).map((o) => o.displayName);
      const extra = occupants.length - names.length;
      const line = names.length > 0 ? names.join(', ') + (extra > 0 ? ` +${extra}` : '') : '';
      if (line !== sign.occupantsLine) {
        sign.occupantsLine = line;
        this.redrawZoneSign(sign);
      }
    }
  }

  /** Hochgeladenes Bild in den Rahmen eines 'picture'-Objekts setzen. */
  setObjectImage(objectId: string, imageId: string | null): void {
    if (!this.app) return;
    const container = this.objectContainers.get(objectId);
    if (!container) return;
    const previous = this.objectImageSprites.get(objectId);
    if (previous) {
      previous.destroy();
      this.objectImageSprites.delete(objectId);
    }
    if (!imageId) return;
    const size = this.objectSizes.get(objectId);
    if (!size) return;
    void Assets.load<Texture>(`/api/images/${encodeURIComponent(imageId)}`).then((texture) => {
      // Waehrenddessen kann die Map neu geladen oder das Bild erneut gewechselt sein
      const current = this.objectContainers.get(objectId);
      if (!current || current !== container || container.destroyed) return;
      const sprite = new Sprite(texture);
      // In die Passepartout-Flaeche einpassen (10 % Rahmen)
      sprite.width = size.w * 0.8;
      sprite.height = size.h * 0.8;
      sprite.position.set(size.w * 0.1, size.h * 0.1);
      container.addChild(sprite);
      this.objectImageSprites.set(objectId, sprite);
    });
  }

  /** Hervorhebung des interagierbaren Objekts ("E druecken"). */
  setObjectHighlight(objectId: string | null): void {
    if (this.highlightedObjectId === objectId) return;
    if (this.highlightedObjectId) {
      const previous = this.highlights.get(this.highlightedObjectId);
      if (previous) previous.visible = false;
    }
    this.highlightedObjectId = objectId;
    if (objectId) this.showHighlight(objectId);
  }

  /** Pro Frame: Avatare anlegen/aktualisieren/entfernen. */
  syncAvatars(states: readonly AvatarRenderState[]): void {
    if (!this.app) return;
    const seen = new Set<string>();
    for (const state of states) {
      seen.add(state.id);
      const configKey = JSON.stringify(state.avatar);
      let entry = this.avatarEntries.get(state.id);
      if (entry && entry.configKey !== configKey) {
        // Avatar-Konfiguration geaendert (selten) -> Visual neu erzeugen
        entry.root.destroy({ children: true });
        this.avatarEntries.delete(state.id);
        entry = undefined;
      }
      if (!entry) {
        entry = this.createAvatarEntry(state, configKey);
        this.avatarEntries.set(state.id, entry);
        this.layers.avatars.addChild(entry.root);
      }
      entry.root.position.set(state.x * TILE_SIZE, state.y * TILE_SIZE);
      entry.root.zIndex = state.y;
      entry.visual.setPose(state.direction, state.moving);
      entry.visual.setSpeaking(state.speaking);
      if (entry.dndBadge.visible !== state.dnd) entry.dndBadge.visible = state.dnd;
    }
    for (const [id, entry] of this.avatarEntries) {
      if (!seen.has(id)) {
        entry.root.destroy({ children: true });
        this.avatarEntries.delete(id);
      }
    }
  }

  private createAvatarEntry(state: AvatarRenderState, configKey: string): AvatarEntry {
    const visual = this.theme.createAvatar(state.avatar);
    const root = new Container();
    const label = new Text({
      text: state.displayName,
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fontWeight: state.isSelf ? '600' : '500',
        fill: 0xffffff,
        stroke: { color: WORLD.shadow, width: 3 },
      },
    });
    label.anchor.set(0.5, 1);
    // Figur ist ~1,5 Tiles hoch — Label ueber dem Kopf
    label.position.set(0, -TILE_SIZE * 1.65);
    // "Bitte anklingeln"-Kennzeichnung neben dem Namen
    const dndBadge = new Text({ text: '🔕', style: { fontSize: 10 } });
    dndBadge.anchor.set(0, 1);
    dndBadge.position.set(label.width / 2 + 3, -TILE_SIZE * 1.65);
    dndBadge.visible = state.dnd;
    root.addChild(visual.view, label, dndBadge);
    return { visual, label, dndBadge, root, configKey };
  }

  /**
   * Kurzlebiger Klick-Marker (pulsierender Ring) am Ziel des Auto-Walks.
   * Ein neuer Klick ersetzt den vorigen Marker.
   */
  showClickMarker(worldX: number, worldY: number): void {
    if (this.clickMarker) {
      this.clickMarker.destroy();
      this.clickMarker = null;
    }
    const gfx = new Graphics().circle(0, 0, TILE_SIZE * 0.35).stroke({ width: 3, color: WORLD.screenGlow });
    gfx.position.set(worldX * TILE_SIZE, worldY * TILE_SIZE);
    this.layers.overlay.addChild(gfx);
    this.clickMarker = gfx;
    this.clickMarkerStart = -1; // beim naechsten updateEffects stempeln
  }

  /** Pro Frame: Marker-Animation fortschreiben (nur transform/alpha). */
  updateEffects(nowMs: number): void {
    const gfx = this.clickMarker;
    if (!gfx) return;
    if (gfx.destroyed) {
      this.clickMarker = null;
      return;
    }
    if (this.clickMarkerStart < 0) this.clickMarkerStart = nowMs;
    const t = (nowMs - this.clickMarkerStart) / 600;
    if (t >= 1) {
      gfx.destroy();
      this.clickMarker = null;
      return;
    }
    gfx.scale.set(0.6 + 0.6 * t);
    gfx.alpha = 1 - t;
  }

  /** Pro Frame: der Kamera weich zum Avatar folgen und zeichnen. */
  applyCamera(pose: ViewPose): void {
    this.projection.follow(pose.x, pose.y);
    this.applyCameraState(this.projection);
  }

  /**
   * Kamera-Transform anwenden. Oeffentlich, weil der Map-Editor eine eigene,
   * frei bewegte Kamera mitbringt statt einem Avatar zu folgen.
   */
  applyCameraState(camera: Camera): void {
    if (!this.app) return;
    camera.setViewport(this.app.renderer.width / this.app.renderer.resolution, this.app.renderer.height / this.app.renderer.resolution);
    const scale = camera.zoom;
    this.world.scale.set(scale);
    this.world.position.set(
      camera.viewportWidth / 2 - camera.x * TILE_SIZE * scale,
      camera.viewportHeight / 2 - camera.y * TILE_SIZE * scale,
    );
    this.layers.avatars.sortableChildren = true;
  }

  /**
   * Rendert eine Avatar-Figur ueber den BESTEHENDEN Renderer in ein Bild
   * (data-URL). Bewusst kein eigener Pixi-/WebGL-Kontext fuer die Vorschau —
   * ein zweiter Kontext kann beim Schliessen den Haupt-Canvas verlieren lassen.
   */
  snapshotAvatar(config: AvatarConfig): string {
    if (!this.app) return '';
    const visual = this.theme.createAvatar(config);
    visual.setPose('down', false);
    const wrap = new Container();
    wrap.addChild(visual.view);
    try {
      const canvas = this.app.renderer.extract.canvas({ target: wrap, antialias: true });
      return typeof canvas.toDataURL === 'function' ? canvas.toDataURL() : '';
    } catch {
      return '';
    } finally {
      wrap.destroy({ children: true });
    }
  }

  destroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.avatarEntries.clear();
    this.objectContainers.clear();
    this.highlights.clear();
    // Die gebackenen Texturen gehoeren dem Theme; ohne diesen Aufruf bleibt
    // bei jedem Ansichtswechsel ein kompletter Satz zurueck
    this.theme.destroy?.();
    this.app?.destroy(true, { children: true });
    this.app = null;
  }
}
